import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { COMPANY } from "../../../../../constants/legal";
import { whatsappChatUrl } from "../../../../../constants/support";
import { useDismissable } from "../hooks/useDismissable";

/**
 * "Contact us", as a choice: a WhatsApp chat, or a message through the Support
 * tab. Both open in a new tab so the wizard — and whatever was typed into it —
 * stays where it is.
 *
 * Support rather than the public contact form: the form lives on the landing
 * page, which sends a signed-in visitor straight back to the dashboard, and a
 * ticket arrives already tied to this account.
 */
const ContactMenu = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismissable(open, close);

  const itemClass =
    "flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left no-underline transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("Dashboard.Setup.topBar.contact")}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center gap-2 rounded-lg px-2.5 text-sm font-medium text-ink-700 transition hover:bg-ink-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
      >
        <i className="bi bi-chat-dots text-lg" aria-hidden="true" />
        <span className="hidden md:inline">
          {t("Dashboard.Setup.topBar.contact")}
        </span>
        <i className="bi bi-chevron-down hidden text-xs md:inline" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 z-40 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-card border border-ink-200 bg-white p-2 shadow-card-hover"
        >
          <a
            role="menuitem"
            href={whatsappChatUrl(t("Dashboard.Setup.topBar.whatsappMessage"))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={itemClass}
          >
            <i className="bi bi-whatsapp mt-0.5 text-lg text-green-600" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink-900">
                {t("Dashboard.Setup.topBar.whatsapp")}
              </span>
              <span className="block text-xs text-ink-500">{COMPANY.phone}</span>
            </span>
          </a>
          <a
            role="menuitem"
            href="/dashboard/support"
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={itemClass}
          >
            <i className="bi bi-envelope mt-0.5 text-lg text-brand-600" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink-900">
                {t("Dashboard.Setup.topBar.message")}
              </span>
              <span className="block text-xs text-ink-500">
                {t("Dashboard.Setup.topBar.messageHint")}
              </span>
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

export default ContactMenu;
